/* eslint-disable no-useless-return */

import React from "react"
import type { GatsbyBrowser } from "gatsby"
import ResizeObserver from "resize-observer-polyfill"
import * as Sentry from "@sentry/gatsby"
import type { SSRData } from "types/ssrTypes"
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import sentryConfig from "./sentry-config-browser"
import {
  GlobalRootProviders,
  GlobalPageProviders
} from "components/GlobalProviders"
import {
  isProduction,
  DYNAMIC_YIELD_PRODUCTION_SECTION_ID,
  DYNAMIC_YIELD_STAGING_SECTION_ID,
  ENABLE_DYNAMIC_YIELD
} from "lib/environment"
import { dynamicYieldDebugLog } from "lib/helpers"
import { bootAnalytics } from "cookiesConsent"
import { FeatureFlagReadModelFactory } from "featureFlags"

Sentry.init(sentryConfig)

if (!window.ResizeObserver) {
  window.ResizeObserver = ResizeObserver
}

// Using wrapPageElement rather than wrapRootElement to provide access to the useLocation hook
// https://github.com/gatsbyjs/gatsby/issues/26963#issuecomment-696037008
// eslint-disable-next-line import/prefer-default-export
export const wrapPageElement: GatsbyBrowser[`wrapPageElement`] = ({
  element,
  props
}) => {
  const serverSideProps = props as SSRData

  return (
    <GlobalPageProviders
      serverSideDynamicYieldProps={
        serverSideProps?.serverData?.dynamicYieldData
      }
    >
      {element}
    </GlobalPageProviders>
  )
}

export const wrapRootElement: GatsbyBrowser[`wrapRootElement`] = ({
  element
}) => <GlobalRootProviders>{element}</GlobalRootProviders>

const createDyObject = () => {
  dynamicYieldDebugLog(`Inserting window.DY object`)
  const script = document.createElement(`script`)
  script.innerHTML = `window.DY = window.DY || {};`
  document.head.appendChild(script)
}

const initialiseExperimentationAPI = () => {
  if (ENABLE_DYNAMIC_YIELD) {
    createDyObject()
  } else {
    return
  }
}

export const onClientEntry = () => {
  bootAnalytics(new FeatureFlagReadModelFactory().build())
  initialiseExperimentationAPI()
}

const injectDynamicYieldScript = () => {
  dynamicYieldDebugLog(`Inserting static and dynamic scripts`)
  const dynamicYieldSection = isProduction()
    ? DYNAMIC_YIELD_PRODUCTION_SECTION_ID
    : DYNAMIC_YIELD_STAGING_SECTION_ID

  if (dynamicYieldSection) {
    const dynamicScript = document.createElement(`script`)
    dynamicScript.src = `https://cdn-eu.dynamicyield.com/api/${dynamicYieldSection}/api_dynamic.js`
    document.head.appendChild(dynamicScript)

    const staticScript = document.createElement(`script`)
    staticScript.src = `https://cdn-eu.dynamicyield.com/api/${dynamicYieldSection}/api_static.js`
    document.head.appendChild(staticScript)
  }
}

const initialiseExperimentationProvider = () => {
  if (ENABLE_DYNAMIC_YIELD) {
    injectDynamicYieldScript()
  } else {
    return
  }
}

export const onInitialClientRender = () => {
  initialiseExperimentationProvider()
}
